import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useLogout } from "../hooks/useLogout";
import type { AuthUser, LinkedCharacter } from "../hooks/useAuth";

interface PilotMenuProps {
  characters: LinkedCharacter[];
  activeCharacterId: number;
  activeName: string;
  isServerOwner: boolean;
  onOpenAddCharacters: () => void;
}

export function PilotMenu({
  characters,
  activeCharacterId,
  activeName,
  isServerOwner,
  onOpenAddCharacters,
}: PilotMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const logout = useLogout();
  const setActive = useSetActiveCharacter();

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="pilot-menu" ref={rootRef}>
      <button
        type="button"
        className="pilot-menu__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <img
          className="topbar__avatar"
          src={`https://images.evetech.net/characters/${activeCharacterId}/portrait?size=64`}
          alt=""
        />
        <span className="topbar__pilot">{activeName}</span>
      </button>
      {open && (
        <div className="pilot-menu__dropdown">
          {characters.map((c) => (
            <button
              key={c.character_id}
              type="button"
              className="pilot-menu__item pilot-menu__item--character"
              aria-current={
                c.character_id === activeCharacterId ? "true" : undefined
              }
              disabled={c.character_id === activeCharacterId}
              onClick={() => {
                setOpen(false);
                if (c.character_id !== activeCharacterId) {
                  setActive.mutate(c.character_id);
                }
              }}
            >
              <img
                className="pilot-menu__avatar"
                src={`https://images.evetech.net/characters/${c.character_id}/portrait?size=64`}
                alt=""
              />
              <span className="pilot-menu__item-name">{c.name}</span>
              {!c.esi_token_ok && (
                <span className="pilot-menu__token-warn" title="ESI token expired">
                  !
                </span>
              )}
            </button>
          ))}
          <button
            type="button"
            className="pilot-menu__item pilot-menu__item--link"
            aria-haspopup="dialog"
            onClick={() => {
              setOpen(false);
              onOpenAddCharacters();
            }}
          >
            Add Characters…
          </button>
          {isServerOwner && (
            <a
              className="pilot-menu__item pilot-menu__item--link"
              href="#/admin/access"
              onClick={() => setOpen(false)}
            >
              Server access
            </a>
          )}
          <button
            type="button"
            className="pilot-menu__item"
            onClick={() => {
              setOpen(false);
              logout.mutate();
            }}
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

function useSetActiveCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (characterId: number) => {
      return api<{ ok: boolean; active_character_id: number }>(
        "/account/active-character",
        {
          method: "POST",
          body: JSON.stringify({ character_id: characterId }),
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
      qc.invalidateQueries({ queryKey: ["account"] });
    },
  });
}

export type { AuthUser };
